// CREATE A ONE-OFF PART THE ERP DOES NOT CARRY (slice 1d-b).
//
// The FIRST write path from this app into the product master. Everything
// unusual in this file follows from that.
//
// IN NewQuoteModal'S SHAPE, DELIBERATELY NOT SHARING A SHELL WITH IT. A shared
// Modal.tsx plus a first write path in one slice would make a regression in
// either indistinguishable from a regression in the other. DetailPanel does
// not fit (a fixed 420px full-height aside), and ProductDetailPanel is
// display-only -- repurposing it into a half-writable sixth panel is exactly
// what this file exists to avoid.
//
// WHAT IT DOES NOT SEND, and why each absence is structural:
//
//   source / created_by_uid   Stamped SERVER-SIDE from the code path and the
//                             session. Neither is in the backend's input
//                             allowlist, so sending one is dropped -- a caller
//                             that could set "erp" could dress a hand-typed
//                             one-off as ERP master data.
//   any price                 There is no price column on anixify.product and
//                             never will be. Every price in this system comes
//                             from the engine.
//   active                    A create must not produce an ARCHIVED product:
//                             /anixify/api/products filters on active=True, so
//                             the row would be written and then be invisible
//                             to the screen that just created it.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import {
  createProduct,
  getTaxonomy,
  searchProducts,
} from "@/services/api";
import {
  intersectTaxonomy,
  emptyIntersectMessage as buildEmptyIntersectMessage,
} from "@/services/productTaxonomy";
import type { CatalogProduct } from "@/types";
import { colors, semanticColors, radius, spacing, heights, typography, shadows } from "@/styles/tokens";

// How many rows the duplicate check asks for. Small on purpose: it is looking
// for an EXACT part-number match, not browsing, and the endpoint is `ilike`
// over part_number OR description, so a broad page would be paid for and
// discarded.
const DUP_CHECK_LIMIT = 20;

// Matches the catalog's own debounce. A part number is typed character by
// character and every intermediate prefix would otherwise be a request.
const DUP_CHECK_DEBOUNCE_MS = 300;

// THE NOTCHED OUTLINE. The label sits ON the field's top border, painted in
// the panel's own background so it cuts a gap in the line rather than covering
// it. That is why it reads `panelBg` and not `surface`: the strip of colour
// immediately above a field's edge belongs to the panel, so a `surface` label
// would show as a white smear on a canvas sheet.
//
// ⚠️ THE LABEL IS A REAL <label htmlFor>, NOT A PLACEHOLDER. Placeholders
// vanish the moment someone types, which is exactly when they'd want to check
// which field they are in; every field here keeps its visible name at all
// times and the placeholder stays a hint on top of it.
const FIELD_LABEL: React.CSSProperties = {
  position: "absolute", top: -spacing[6], left: spacing[10], zIndex: 1,
  padding: `0 ${spacing[4]}px`, backgroundColor: semanticColors.panelBg,
  fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
  fontWeight: typography.fontWeight.medium, pointerEvents: "none",
  maxWidth: `calc(100% - ${spacing[20]}px)`,
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

/** The field box. `focused` and `invalid` are presentation-only — no gate,
 *  no submit and no payload reads either of them. */
function fieldStyle(focused: boolean, disabled: boolean): React.CSSProperties {
  return {
    width: "100%", height: heights.inputLg,
    padding: `0 ${spacing[14]}px`,
    fontSize: typography.fontSize.md, color: semanticColors.textPrimary,
    // Focus is carried by BOTH the border colour and the halo. A halo alone
    // would be a colour-only cue, and a border alone is easy to miss at 1px.
    border: `1px solid ${focused ? semanticColors.accent : semanticColors.border}`,
    boxShadow: focused ? shadows.focusRing : "none",
    borderRadius: radius.lg,
    backgroundColor: semanticColors.panelBg,
    outline: "none", boxSizing: "border-box",
    // Disabled is signalled three ways — dimmed, cursor, and the native
    // `disabled` attribute the control already carries for assistive tech.
    opacity: disabled ? 0.55 : 1,
    cursor: disabled ? "not-allowed" : undefined,
    transition: "border-color 120ms ease-out, box-shadow 120ms ease-out",
  };
}

// RESPONSIVE BY CONSTRUCTION, not by breakpoint. `auto-fit` + a 200px floor
// means a pair of fields becomes a stack whenever the panel is too narrow to
// hold both legibly — including inside the shell's own `maxWidth: 100%` on a
// small viewport, where a hard `1fr 1fr` would instead squeeze two fields to
// an unusable width. Also why the row gap is the larger of the two: the
// notched labels sit ABOVE each field's box and need clearance from whatever
// wraps onto the line above.
const FIELD_GRID: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  columnGap: spacing[14],
  rowGap: spacing[24],
};

/** Shared shape for the three notice boxes (taxonomy error, empty intersect,
 *  create error) so a fourth cannot drift. */
function noticeStyle(tone: "alert" | "advisory"): React.CSSProperties {
  const alert = tone === "alert";
  return {
    padding: `${spacing[10]}px ${spacing[12]}px`,
    backgroundColor: alert ? semanticColors.destructiveBg : semanticColors.warningBg,
    border: `1px solid ${alert ? semanticColors.destructiveEdge : semanticColors.warningBorder}`,
    borderRadius: radius.lg,
    fontSize: typography.fontSize.base,
    color: alert ? semanticColors.destructive : semanticColors.warningTextOnTint,
  };
}

// The two footer buttons. Extracted as builders rather than left inline
// because both are read twice — once for the enabled look, once for the
// disabled one — and the disabled branch is the half that gets forgotten.
//
// ⚠️ NEITHER SETS A TEXT COLOUR ON THE PRIMARY. The accent is a light green
// now, so the readable pairing is dark-on-accent; that lives on `.btn-primary`
// in index.css, which is also what reaches the sixteen other primary buttons
// in the app that still hard-code white inline. Setting `color` here would
// win over the stylesheet and put this one button back to 1.6:1.
const PRIMARY_BUTTON = (enabled: boolean): React.CSSProperties => ({
  // controlXl, not control: 44px is the touch-target floor, and this is the
  // button that commits a write.
  height: heights.controlXl, padding: `0 ${spacing[24]}px`,
  backgroundColor: enabled ? semanticColors.accent : semanticColors.textDisabled,
  border: "none", borderRadius: radius.lg,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
  cursor: enabled ? "pointer" : "not-allowed",
});

const SECONDARY_BUTTON = (busy: boolean): React.CSSProperties => ({
  height: heights.controlXl, padding: `0 ${spacing[16]}px`,
  backgroundColor: "transparent",
  color: busy ? semanticColors.textDisabled : semanticColors.textSecondary,
  border: "none", borderRadius: radius.lg,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
  cursor: busy ? "not-allowed" : "pointer",
});

/** ⚠️ The asterisk is `aria-hidden` and paired with `required` on the control
 *  itself, so a screen reader announces "required" rather than reading a
 *  punctuation mark — and sighted users still get the mark the reference
 *  shows. Marking required is not the same as ENFORCING it: the gate is
 *  `canCreate`, unchanged, and the browser never blocks this form. */
function FieldLabel({ htmlFor, children, required }: {
  htmlFor: string; children: React.ReactNode; required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} style={FIELD_LABEL}>
      {children}
      {required && <span aria-hidden="true" style={{ color: semanticColors.destructive }}>*</span>}
    </label>
  );
}

function TextField({
  label, testid, value, onChange, placeholder, required, disabled, warning,
}: {
  label: string; testid: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; disabled?: boolean; warning?: string;
}) {
  const [focused, setFocused] = useState(false);
  const warningId = `${testid}-warning`;
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ position: "relative" }}>
        <FieldLabel htmlFor={testid} required={required}>{label}</FieldLabel>
        <input
          id={testid}
          data-testid={testid}
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-describedby={warning ? warningId : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          style={fieldStyle(focused, !!disabled)}
        />
      </div>
      {/* NON-BLOCKING, and rendered the same way CustomerPicker renders the
          GSTIN advisory -- amber, beneath the field, submit untouched.
          aria-live so it is announced when it appears, since it arrives
          asynchronously after a debounce rather than on a keystroke. */}
      {warning && (
        <div
          id={warningId}
          data-testid={warningId}
          aria-live="polite"
          style={{
            marginTop: spacing[6], fontSize: typography.fontSize.sm,
            color: colors.amber600, lineHeight: 1.4,
          }}
        >
          {warning}
        </div>
      )}
    </div>
  );
}

function SelectField({
  label, testid, value, options, onChange, placeholder, required, disabled,
}: {
  label: string; testid: string; value: string; options: readonly string[];
  onChange: (v: string) => void; placeholder?: string; required?: boolean; disabled?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
      <div style={{ position: "relative" }}>
        <FieldLabel htmlFor={testid} required={required}>{label}</FieldLabel>
        <select
          id={testid}
          data-testid={testid}
          value={value}
          disabled={disabled}
          required={required}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(e) => onChange(e.target.value)}
          style={{
            ...fieldStyle(focused, !!disabled),
            // Native chevron kept deliberately: `appearance: none` would need
            // a hand-drawn one, and the platform's own control is the thing
            // users already recognise as "this opens a list".
            paddingRight: spacing[8],
            // An unchosen select shows the placeholder option, which is a hint
            // and not a value — tone it to match the text inputs' placeholder.
            color: value === "" ? semanticColors.textPlaceholder : semanticColors.textPrimary,
          }}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

/** The form, flat and stringly-typed -- it is a form. It becomes the backend
 *  payload in exactly one place (buildPayload) rather than being assembled in
 *  the JSX, the same call NewCustomerDraft makes. */
export interface ProductDraft {
  part_number: string;
  brand: string;
  category: string;
  description: string;
  manufacturer: string;
  supplier_ref: string;
  hsn_code: string;
  default_origin: string;
  uom: string;
  weight: string;
  volume: string;
}

/** ⚠️ THE BASE EVERY DRAFT SPREADS OVER, and exported for that reason.
 *  buildPayload indexes every key (`draft[key].trim()`), so a PARTIAL draft --
 *  a CSV row missing a column -- throws rather than omitting the field. The
 *  import path spreads its row over this. */
export const EMPTY_DRAFT: ProductDraft = {
  part_number: "", brand: "", category: "", description: "", manufacturer: "",
  supplier_ref: "", hsn_code: "", default_origin: "", uom: "", weight: "", volume: "",
};

/** ⚠️ ABSENT IS ABSENT. A key reaches the payload only when it has a real
 *  value: strings are trimmed and omitted when empty, numerics are parsed and
 *  omitted when blank or unparseable.
 *
 *  Sending "" or 0 instead would be inventing a measurement nobody took --
 *  and 0 is indistinguishable from a real zero once it is in the column, which
 *  is precisely why /catalog dashes a zero weight rather than printing it. The
 *  backend's allowlist drops unknown keys but happily stores a supplied zero,
 *  so this is the only place the distinction survives. */
export function buildPayload(draft: ProductDraft): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  const str = (key: keyof ProductDraft, wireKey: string) => {
    const v = draft[key].trim();
    if (v !== "") payload[wireKey] = v;
  };

  // Required. Trimmed here as well as server-side: "   " is truthy, and a
  // product whose identity is whitespace is blank in every table and
  // unfindable by the search that matches on that column.
  str("part_number", "part_number");
  // Taxonomy NAMES, not ids -- the backend resolves them and refuses an
  // unknown one rather than creating master data from a typo.
  str("brand", "brand");
  str("category", "category");
  str("description", "description");
  str("manufacturer", "manufacturer");
  str("supplier_ref", "supplier_ref");
  str("hsn_code", "hsn_code");
  str("default_origin", "default_origin");
  str("uom", "uom");

  const num = (key: "weight" | "volume") => {
    const raw = draft[key].trim();
    if (raw === "") return;
    const parsed = Number(raw);
    // NaN would serialize as null and reach the ORM as a nonsense write.
    // An unparseable measurement is an ABSENT one, not a zero.
    if (!Number.isFinite(parsed)) return;
    payload[key] = parsed;
  };
  num("weight");
  num("volume");

  return payload;
}

export function NewProductModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (p: CatalogProduct) => void;
}) {
  const [draft, setDraft] = useState<ProductDraft>(EMPTY_DRAFT);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  // ---- The taxonomy --------------------------------------------------------
  //
  // Loaded rather than restated, so an archived or renamed brand stops being
  // offered without a frontend deploy. A failure DISABLES the form: there is
  // no other list to guess from (P1-9), and guessing would offer a brand the
  // server may have archived, failing the save after the form was filled in.
  const [taxStatus, setTaxStatus] = useState<"loading" | "ok" | "error">("loading");
  const [taxError, setTaxError] = useState("");
  const [brands, setBrands] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  // Bumped by Retry. A counter rather than re-setting a piece of state to its
  // current value: React bails out of an Object.is-equal update, so the effect
  // would not re-run and the button would silently do nothing.
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let live = true;
    setTaxStatus("loading");
    (async () => {
      const result = await getTaxonomy();
      if (!live) return;
      if (!result.ok || !result.brands || !result.categories) {
        // CLEARED, not left standing. Stale options under an error message
        // read as current ones and would be submitted against.
        setBrands([]);
        setCategories([]);
        setTaxError(result.error ?? "The brand and category list could not be loaded.");
        setTaxStatus("error");
        return;
      }
      setBrands(result.brands);
      setCategories(result.categories);
      setTaxError("");
      setTaxStatus("ok");
    })();
    return () => { live = false; };
  }, [retryTick]);

  // ⚠️ THE INTERSECT, AND BOTH HALVES ARE LOAD-BEARING. They fail in opposite
  // directions: without the backend list, an archived or renamed brand is
  // still offered and the save fails at the server; without the KNOWN_ filter,
  // a backend-only brand creates a product that brand-lock evaluation cannot
  // see -- the silent direction, because no rule matches and a Strict lock
  // quietly stops firing.
  //
  // The rule itself now lives in services/productTaxonomy.ts, because the CSV
  // import needs the same one in a pure per-row validator where a useMemo
  // cannot go. It is still memoized HERE -- this is the only change, and the
  // values are identical.
  const { brandOptions, categoryOptions } = useMemo(
    () => intersectTaxonomy(brands, categories),
    [brands, categories],
  );

  const emptyIntersect = taxStatus === "ok"
    && (brandOptions.length === 0 || categoryOptions.length === 0);

  // Names which side is empty. "Nothing is available" would leave someone
  // guessing at which list to go fix in Odoo.
  const emptyIntersectMessage = useMemo(() => {
    if (!emptyIntersect) return "";
    return buildEmptyIntersectMessage(brandOptions, categoryOptions, brands, categories);
  }, [emptyIntersect, brandOptions, categoryOptions, brands, categories]);

  // ---- The duplicate check -------------------------------------------------
  //
  // ADVISORY, NEVER BLOCKING. Part numbers are legitimately not unique -- two
  // manufacturers ship different goods under one string and the backend
  // deliberately has no constraint saying otherwise. The precedent for both
  // the wording and the non-blocking-ness is gstinLooksWrong in CustomerPicker.
  const [dupPartNo, setDupPartNo] = useState("");
  const partNo = draft.part_number.trim();

  useEffect(() => {
    if (partNo === "") { setDupPartNo(""); return; }
    let live = true;
    const timer = setTimeout(async () => {
      const result = await searchProducts(partNo, DUP_CHECK_LIMIT);
      if (!live) return;
      // ⚠️ EXACT MATCH ONLY. The endpoint is `ilike` over part_number OR
      // description, so "8G7" matches "8G7T9AA" and a description mentioning
      // it -- neither is a duplicate. Warning on a substring hit would train
      // people to ignore the warning that matters.
      //
      // A FAILED CHECK SAYS NOTHING. It is advisory, so a network failure must
      // not produce a warning ("there is a duplicate") or suppress a real one
      // in a way anybody relies on -- and it must never block the create.
      const hit = result.ok && !!result.products?.some((p) => p.partNo === partNo);
      setDupPartNo(hit ? partNo : "");
    }, DUP_CHECK_DEBOUNCE_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [partNo]);

  const duplicateWarning = dupPartNo !== "" && dupPartNo === partNo
    ? "A product with this part number already exists. It will still be saved."
    : undefined;

  // ---- Closing -------------------------------------------------------------
  //
  // ⚠️ BLOCKED WHILE A CREATE IS IN FLIGHT. Backdrop, Escape and Cancel all
  // no-op. Closing mid-request would abandon a POST whose result nobody ever
  // sees: the row may well have been written, and the screen behind would show
  // no error, no new product and no reason to look for one.
  //
  // The rule itself now lives in ui/Modal.tsx, which derives the same gate
  // from `busy` and applies it to the backdrop, Escape and the X. This copy
  // survives because CANCEL is ours: the footer buttons stay with the caller
  // (they need their own testids and their own disabled rules), so the button
  // that sits next to Create still needs the gate locally. The two agree
  // because they are the same expression over the same flag — and the Escape
  // listener that used to live here is GONE, not duplicated, or Escape would
  // now call onClose twice.
  const requestClose = useCallback(() => {
    if (creating) return;
    onClose();
  }, [creating, onClose]);

  const formDisabled = taxStatus !== "ok" || emptyIntersect || creating;

  const canCreate = taxStatus === "ok"
    && !emptyIntersect
    && partNo !== ""
    && draft.brand !== ""
    && draft.category !== ""
    && !creating;

  const setField = (k: keyof ProductDraft) => (v: string) =>
    setDraft((cur) => ({ ...cur, [k]: v }));

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    setError("");
    const result = await createProduct(buildPayload(draft));
    setCreating(false);
    if (!result.ok || !result.product) {
      // FAIL LOUD: the modal STAYS OPEN carrying the backend's own message,
      // and nothing is inserted anywhere. There is no optimistic row and no
      // fallback -- the returned record is the only proof one exists.
      setError(result.error ?? "Could not create this product.");
      return;
    }
    onCreated(result.product);
    onClose();
  };

  return (
    // The shell owns the backdrop, the panel, the header (title + X), the
    // scroll region, Escape, the backdrop click, focus, and the `busy` gate
    // over all three close routes. It reproduces new-product-backdrop /
    // -modal / -title / -close from `testIdPrefix`, unchanged.
    <Modal
      testIdPrefix="new-product"
      title="Add Product"
      onClose={onClose}
      busy={creating}
      footer={
        <>
          <button
            type="button"
            data-testid="new-product-cancel"
            className="btn-secondary"
            onClick={requestClose}
            disabled={creating}
            style={SECONDARY_BUTTON(creating)}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="new-product-create"
            className="btn-primary"
            onClick={handleCreate}
            disabled={!canCreate}
            style={PRIMARY_BUTTON(canCreate)}
          >
            {creating ? "Creating…" : "Create Product"}
          </button>
        </>
      }
    >
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[24] }}>
            {taxStatus === "loading" && (
              <div
                data-testid="new-product-taxonomy-loading"
                aria-live="polite"
                style={{ fontSize: typography.fontSize.md, color: semanticColors.textPlaceholder }}
              >
                Loading brands and categories…
              </div>
            )}

            {/* No fallback to the build's own lists: offering a brand the
                server has since archived would fail at the save, after the
                form was filled in. Retrying is the only honest offer. */}
            {taxStatus === "error" && (
              <div
                data-testid="new-product-taxonomy-error"
                role="alert"
                style={noticeStyle("alert")}
              >
                <strong>Brands and categories could not be loaded.</strong>{" "}
                <span data-testid="new-product-taxonomy-error-detail">{taxError}</span>{" "}
                <button
                  type="button"
                  data-testid="new-product-taxonomy-retry"
                  onClick={() => setRetryTick((n) => n + 1)}
                  style={{
                    background: "none", border: "none", padding: 0,
                    color: semanticColors.destructive, textDecoration: "underline",
                    cursor: "pointer", font: "inherit", fontWeight: typography.fontWeight.semibold,
                  }}
                >
                  Retry
                </button>
              </div>
            )}

            {/* An empty intersection is a real, explainable state, not an
                error: the server answered, and nothing it offered is a name
                this build can evaluate a brand lock against. Submitting
                something unclassifiable is the one thing that must not
                happen, so it names which side is empty instead.
                Toned as ADVISORY rather than alert for that reason — nothing
                failed, the answer is just unusable. */}
            {emptyIntersect && (
              <div
                data-testid="new-product-empty-taxonomy"
                role="alert"
                style={noticeStyle("advisory")}
              >
                {emptyIntersectMessage}
              </div>
            )}

            <TextField
              label="Part No." testid="np-part-number" value={draft.part_number}
              onChange={setField("part_number")} placeholder="e.g. 8G7T9AA" required
              disabled={formDisabled} warning={duplicateWarning}
            />

            {/* The two required selects sit together, then Description spans
                the row — the reference's rhythm. `auto-fit`/`minmax` rather
                than a fixed 1fr 1fr so the pair collapses to one column on a
                narrow viewport instead of squeezing two unreadable selects. */}
            <div style={FIELD_GRID}>
              <SelectField
                label="Brand" testid="np-brand" value={draft.brand}
                options={brandOptions} onChange={setField("brand")}
                placeholder="— select a brand —" required disabled={formDisabled}
              />

              <SelectField
                label="Category" testid="np-category" value={draft.category}
                options={categoryOptions} onChange={setField("category")}
                placeholder="— select a category —" required disabled={formDisabled}
              />
            </div>

            <TextField
              label="Description" testid="np-description" value={draft.description}
              onChange={setField("description")} disabled={formDisabled}
              placeholder="e.g. Cat6A U/UTP 4-pair LSZH cable"
            />

            <div style={FIELD_GRID}>
              <TextField
                label="Manufacturer" testid="np-manufacturer" value={draft.manufacturer}
                onChange={setField("manufacturer")} disabled={formDisabled}
              />
              <TextField
                label="Supplier Ref" testid="np-supplier-ref" value={draft.supplier_ref}
                onChange={setField("supplier_ref")} disabled={formDisabled}
              />
              <TextField
                label="HSN Code" testid="np-hsn-code" value={draft.hsn_code}
                onChange={setField("hsn_code")} disabled={formDisabled}
              />
              <TextField
                label="UoM" testid="np-uom" value={draft.uom}
                onChange={setField("uom")} placeholder="e.g. EA" disabled={formDisabled}
              />
              <TextField
                label="Default Origin" testid="np-default-origin" value={draft.default_origin}
                onChange={setField("default_origin")} disabled={formDisabled}
              />
              {/* Left blank rather than defaulted to 0. An unmeasured part is
                  unmeasured; 0.00 would be a measurement nobody took, which is
                  why /catalog dashes a zero rather than printing it. */}
              <TextField
                label="Weight kg" testid="np-weight" value={draft.weight}
                onChange={setField("weight")} placeholder="leave blank if unmeasured"
                disabled={formDisabled}
              />
              <TextField
                label="Volume CBM" testid="np-volume" value={draft.volume}
                onChange={setField("volume")} placeholder="leave blank if unmeasured"
                disabled={formDisabled}
              />
            </div>

            {/* The create failed and the modal stayed open holding the
                backend's own words. role="alert" so it is announced rather
                than sitting silently below a button someone just pressed. */}
            {error && (
              <div
                data-testid="new-product-error"
                role="alert"
                style={noticeStyle("alert")}
              >
                {error}
              </div>
            )}
          </div>
    </Modal>
  );
}
